import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetTextBlockQuery } from "@generated";
import { TextBlockForm } from "../TextBlockForm/TextBlockForm";
import { useTextBlockMutations } from "../api";
import { emptyTextBlockForm } from "../types";
import type { TextBlockFormValues } from "../types";

const FORM_ID = "text-block-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useTextBlockMutations();
  // refetchOnMountOrArgChange so re-entering Edit (or arriving from a list
  // changed elsewhere) re-requests the record rather than trusting the cache.
  const { data, isLoading, refetch } = useV2GetTextBlockQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<TextBlockFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<TextBlockFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyTextBlockForm;
    return {
      name: attrs.name ?? "",
      mergeBlock: attrs["merge-block"] ?? "",
    };
  }, [data]);

  const slug = data?.data?.attributes?.slug ?? "";

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: TextBlockFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't invalidate
      // the cached GET. Refetch so the cache holds the server-fresh record
      // (including the regenerated slug) for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update text block", err);
      setToast(saveErrorToast(err, "text block"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Text Block"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <TextBlockForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          slug={slug}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
